/**
 * <faq-categories>
 *
 * Sticky category rail for the "FAQ categories" section (ls-faq-categories.liquid).
 * - Click a rail link → smooth-scroll the matching category heading to just below
 *   the sticky header (and, on mobile, below the sticky rail).
 * - Highlights the active category as the questions scroll.
 * - Falls back to native anchor jumps + CSS scroll-margin when JS is unavailable.
 * - Respects prefers-reduced-motion.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const mobileQuery = matchMedia('(max-width: 749px)');

/** Small breathing gap (px) between the pinned mobile rail and a scrolled-to heading. */
const MOBILE_HEADING_GAP = 12;

class FaqCategories extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  #links = [];
  /** @type {HTMLElement[]} */
  #targets = [];
  /** @type {HTMLElement | null} */
  #rail = null;
  /** @type {HTMLElement | null} */
  #pageWrapper = null;
  #stickyGap = 24;
  #headerHeight = 0;
  #scrollOffset = 24;
  #suppressSync = false;
  /** @type {number | null} */
  #rafId = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #suppressTimer;

  connectedCallback() {
    this.#rail = this.querySelector('.ls-faqc__rail');
    this.#links = Array.from(this.querySelectorAll('[data-faqc-link]'));
    this.#targets = Array.from(this.querySelectorAll('[data-faqc-target]'));
    this.#stickyGap = parseInt(this.dataset.stickyGap ?? '24', 10) || 0;

    if (this.#links.length === 0 || this.#targets.length === 0) return;

    // On desktop (≥990px) the theme makes `.page-wrapper` the scroll container
    // (height: 100dvh; overflow-y: auto); below that the window scrolls. Keep a
    // reference so scroll listening and programmatic scrolling target the right one.
    this.#pageWrapper = document.querySelector('.page-wrapper');

    this.#measure();

    this.#links.forEach((link) => link.addEventListener('click', this.#onLinkClick));
    // Listen on both possible scrollers; #syncActive reads viewport-relative rects,
    // so whichever one actually scrolls, the active state stays correct.
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#pageWrapper?.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
    mobileQuery.addEventListener('change', this.#onResize);

    this.#syncActive();
  }

  disconnectedCallback() {
    this.#links.forEach((link) => link.removeEventListener('click', this.#onLinkClick));
    window.removeEventListener('scroll', this.#onScroll);
    this.#pageWrapper?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    mobileQuery.removeEventListener('change', this.#onResize);
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    clearTimeout(this.#suppressTimer);
  }

  /**
   * The element that actually scrolls the page: `.page-wrapper` when the theme has
   * made it the scroll container (desktop), otherwise the window.
   * @returns {HTMLElement | Window}
   */
  #getScroller() {
    const wrapper = this.#pageWrapper;
    if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight + 1) {
      const overflowY = getComputedStyle(wrapper).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return wrapper;
    }
    return window;
  }

  /**
   * Measure how much space a sticky theme header currently occupies at the top.
   * The theme makes `.header-section` `position: sticky` only when the header is
   * set to "always", or to "scroll-up" while active — otherwise it stays static.
   * Reading the computed position reflects the real, breakpoint-aware state, so a
   * non-sticky header (incl. mobile, where the setting may differ) yields 0.
   * @returns {number}
   */
  #measureHeaderOffset() {
    const headerComponent = document.querySelector('#header-component');
    const headerSection =
      headerComponent?.closest('.header-section') ?? document.querySelector('.header-section');
    if (!(headerSection instanceof HTMLElement)) return 0;
    const position = getComputedStyle(headerSection).position;
    if (position === 'sticky' || position === 'fixed') {
      return Math.round(headerSection.getBoundingClientRect().height);
    }
    return 0;
  }

  /** Measure the sticky header (and, on mobile, the sticky rail) to compute the scroll offset. */
  #measure() {
    const headerHeight = this.#measureHeaderOffset();
    this.#headerHeight = headerHeight;
    this.style.setProperty('--faqc-header-h', `${headerHeight}px`);

    let extra = this.#stickyGap;
    if (mobileQuery.matches && this.#rail) {
      // On mobile the rail pins flush under the header (top: header height, no gap),
      // so a scrolled-to heading only needs to clear the rail height plus a small
      // breathing gap — not another full sticky gap.
      const railHeight = Math.round(this.#rail.getBoundingClientRect().height);
      extra = railHeight + MOBILE_HEADING_GAP;
    }
    this.style.setProperty('--faqc-scroll-offset', `${extra}px`);
    this.#scrollOffset = headerHeight + extra;
  }

  #onResize = () => {
    this.#measure();
    this.#syncActive();
  };

  #onScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#syncActive();
    });
  };

  /** @param {MouseEvent} event */
  #onLinkClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLElement)) return;
    const id = link.dataset.faqcLink;
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();

    // Re-measure in case the header/rail size changed since load.
    this.#measure();
    this.#setActive(id);

    // Suppress scroll-driven re-highlighting until the programmatic scroll settles.
    this.#suppressSync = true;
    clearTimeout(this.#suppressTimer);

    const scroller = this.#getScroller();
    const targetTop = target.getBoundingClientRect().top;
    let top;
    if (scroller === window) {
      top = window.scrollY + targetTop - this.#scrollOffset;
    } else {
      // Offset the target's viewport position by the scroller's own viewport position.
      top = scroller.scrollTop + (targetTop - scroller.getBoundingClientRect().top) - this.#scrollOffset;
    }
    top = Math.max(0, top);
    const behavior = reducedMotion.matches ? 'auto' : 'smooth';
    scroller.scrollTo({ top, behavior });

    // Move focus to the heading for keyboard/screen-reader users, without re-scrolling.
    target.focus({ preventScroll: true });

    if (history.replaceState) history.replaceState(null, '', `#${id}`);

    this.#suppressTimer = setTimeout(
      () => {
        this.#suppressSync = false;
        this.#syncActive();
      },
      reducedMotion.matches ? 50 : 700
    );
  };

  /** Highlight the category whose heading is currently at/above the offset line. */
  #syncActive() {
    if (this.#suppressSync || this.#targets.length === 0) return;
    const line = this.#scrollOffset + 4;
    let currentId = this.#targets[0].id;
    for (const target of this.#targets) {
      if (target.getBoundingClientRect().top - line <= 0) {
        currentId = target.id;
      } else {
        break;
      }
    }
    this.#setActive(currentId);
  }

  /** @param {string} id */
  #setActive(id) {
    this.#links.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.faqcLink === id);
    });
  }
}

if (!customElements.get('faq-categories')) {
  customElements.define('faq-categories', FaqCategories);
}
